'use client'

import { Box, Divider, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import FacebookIcon from '@mui/icons-material/Facebook'
import XIcon from '@mui/icons-material/X'
import YouTubeIcon from '@mui/icons-material/YouTube'
import InstagramIcon from '@mui/icons-material/Instagram'
import { useTranslation } from 'react-i18next'
import { usePathname } from 'next/navigation'
import Image from 'next/image'

import useLogo from '@/hooks/useLogo'
import Link from '@/components/common/Link'
import { getLocaleFromPath, defaultLocale } from '@/utils/locale'
import type { Rating } from '@/types/status'
import ratingsData from '@/data/ratings.json'

// Discord and Twitch don't have MUI icons, use simple SVG
const DiscordIcon = () => (
  <svg viewBox='0 0 24 24' width='1em' height='1em' fill='currentColor'>
    <path d='M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z' />
  </svg>
)

const TwitchIcon = () => (
  <svg viewBox='0 0 24 24' width='1em' height='1em' fill='currentColor'>
    <path d='M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0 1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z' />
  </svg>
)

const FooterLink = styled(Link)(({ theme }) => ({
  fontFamily: `${theme.fontFamily.primary} !important`,
  fontWeight: '800',
  fontStretch: 'condensed',
  textDecoration: 'none !important',
  textTransform: 'uppercase',
  fontSize: '1rem',
  padding: 0,
  minWidth: 'unset',
  letterSpacing: 'inherit',
  justifySelf: 'flex-start',
  color: `${theme.palette.text.primary} !important`,
  '&:hover, &:focus-visible': {
    color: theme.palette.primary.main + ' !important'
  }
}))

const SocialLink = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  color: `${theme.palette.text.primary} !important`,
  textDecoration: 'none !important',
  '&:hover, &:focus-visible': {
    color: theme.palette.primary.main + ' !important'
  },
  '& svg': {
    height: '100%',
    width: '100%'
  }
}))

const Section = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingTop: '1.25rem',
  paddingBottom: '1.25rem',
  [theme.breakpoints.up('sm')]: {
    paddingTop: '1.875rem',
    paddingBottom: '1.875rem'
  }
}))

interface FooterSiteProps {
  footerNavMenuItems?: Array<{
    url: string
    title?: string
    label?: string
    target?: string
  }>
}

const FRANCHISE_LINKS = [
  { url: 'https://www.gearsofwar.com/en-us/', label: 'Gears of War' },
  { url: 'https://www.gearsofwar.com/en-us/games/', label: 'Games' },
  { url: 'https://www.gearsofwar.com/en-us/news/', label: 'News' },
  { url: 'https://www.gearsofwar.com/en-us/support/', label: 'Support' },
  { url: 'https://www.thecoalitionstudio.com/careers/', label: 'Careers' }
]

export default function FooterSite({ footerNavMenuItems = [] }: FooterSiteProps) {
  const { t } = useTranslation()
  const coalitionLogo = useLogo('theCoalition')
  const xboxGameStudiosLogo = useLogo('xboxGameStudios')

  const pathname = usePathname()
  const locale = getLocaleFromPath(pathname) || defaultLocale

  const franchiseLinks = footerNavMenuItems.length > 0 ? footerNavMenuItems : FRANCHISE_LINKS

  const rating = (ratingsData.ratings as Rating[]).find((r) => r.locales.includes(locale)) ?? null

  return (
    <>
      {/* Franchise Links */}
      <Section
        sx={{
          display: { xs: 'grid', md: 'flex' },
          gridRowGap: '1.25rem',
          gridTemplateColumns: '1fr 1fr',
          gap: { xs: 2, md: 4 },
          flexWrap: {
            md: 'wrap'
          },
          px: { xs: 2, md: 2.25, lg: 8 }
        }}>
        {franchiseLinks.map((item) => {
          return (
            <FooterLink
              key={item.label || item.title}
              href={item.url}
              aria-label={t(item.label || item.title || '')}
              target={item.target}
              rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
              sx={{
                fontSize: { md: '1rem', lg: '1.5rem' }
              }}>
              {t(item?.label || item?.title || '')}
            </FooterLink>
          )
        })}
      </Section>

      {locale === 'fr-fr' && (
        <>
          <Divider />
          <Section sx={{ px: { xs: 2, md: 2.25, lg: 8 } }}>
            <span>{t('Accessibility: partially compliant')}</span>
          </Section>
        </>
      )}

      <Divider />

      {/* Company Links + Social Media */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', lg: 'row' }
        }}>
        {/* Company Logo Links */}
        <Stack
          component='nav'
          aria-label={t('Company Logo Links')}
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: '1.25rem', md: '1.5rem' }}
          sx={{
            paddingBlock: { xs: '1.25rem', md: 2 },
            justifyContent: 'start',
            alignItems: { md: 'center' },
            px: { xs: 2, md: 2.25, lg: 8 }
          }}>
          {rating && (
            <Link href={rating.url} title={t('slug:rating-age-rating-information', { rating: rating.title })}>
              <Image
                src={rating.image}
                alt={t('slug:rating-age-rating', { rating: rating.title })}
                width={0}
                height={0}
                sizes='72px'
                style={{ width: 'auto', height: 72, maxWidth: '100%' }}
              />
            </Link>
          )}
          <Link href='https://thecoalitionstudio.com' title={t('Go to The Coalition Studio')}>
            <img src={coalitionLogo} alt={t('Go to The Coalition Studio')} role='img' height={34} style={{ maxWidth: '100%' }} />
          </Link>
          <Link href='https://www.xbox.com/xbox-game-studios' title={t('Go to Xbox Game Studios')}>
            <img src={xboxGameStudiosLogo} alt={t('Go to Xbox Game Studios')} role='img' height={25} style={{ maxWidth: '100%' }} />
          </Link>
        </Stack>

        {/* Social Media Links */}
        <Stack
          component='nav'
          aria-label={t('Social media links')}
          direction='row'
          spacing={{ xs: 0, sm: 6 }}
          sx={{
            alignItems: 'center',
            paddingTop: { xs: '1.25rem', sm: 0 },
            paddingBottom: { xs: 2, xl: 0 },
            px: { xs: 2, md: 2.25, lg: 8, xl: 8 },
            justifyContent: { xs: 'space-between', sm: 'flex-start' }
          }}>
          <SocialLink href='https://x.com/gearsofwar' target='_blank' rel='noopener noreferrer' aria-label={t('X')} title={t('X')}>
            <XIcon />
          </SocialLink>
          <SocialLink
            href='https://www.facebook.com/gearsofwar'
            target='_blank'
            rel='noopener noreferrer'
            aria-label={t('Facebook')}
            title={t('Facebook')}>
            <FacebookIcon />
          </SocialLink>
          <SocialLink href='https://www.twitch.tv/gearsofwar/' target='_blank' rel='noopener noreferrer' aria-label={t('Twitch')} title={t('Twitch')}>
            <TwitchIcon />
          </SocialLink>
          <SocialLink
            href='https://www.youtube.com/channel/UC3E8iPQGBdAnOQR_cmkEbsw'
            target='_blank'
            rel='noopener noreferrer'
            aria-label={t('YouTube')}
            title={t('YouTube')}>
            <YouTubeIcon />
          </SocialLink>
          <SocialLink
            href='https://www.instagram.com/gearsofwar'
            target='_blank'
            rel='noopener noreferrer'
            aria-label={t('Instagram')}
            title={t('Instagram')}>
            <InstagramIcon />
          </SocialLink>
          <SocialLink href='https://discord.gg/gearsofwar' target='_blank' rel='noopener noreferrer' aria-label={t('Discord')} title={t('Discord')}>
            <DiscordIcon />
          </SocialLink>
        </Stack>
      </Box>
    </>
  )
}
